/* eslint-disable max-len */
import useNotebookStore from '../../../../stores/notebookStore';
import { NotebookStore } from '../../../../stores/notebookStore/types';
import shallow from 'zustand/shallow';
import { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Assignee,
  DropdownItemOption,
  DropdownOptions,
  GetNotebookSectionResponse,
  MiscTaskCategories,
  NotebookAnalyticsParameters,
  NotebookOperations,
  NotebookTask,
  NotebookTaskAnalyticsParameters,
  NotebookViews,
  TaskCategories,
  TaskFromAPI,
} from '../../../../interfaces/notebook';
import { InfiniteData, useQueryClient } from 'react-query';
import {
  convertAPITaskToTaskAndCategory,
  convertStringToTokenizedObject,
  getNotebookAnalyticsProps,
  getNotebookTaskAnalyticsProps,
  isNotebookEditorEmpty,
} from '../../../../Utils/notebook';
import { useProfileInfoFetchQuery } from '../../../../queries/Profile';
import {
  useGetNotebookBoardViewTasksQuery,
  useGetNotebookTaskAssigneeList,
  usePostNotebookSectionsMutation,
} from '../../../../queries/Notebook';
import isEmpty from 'lodash/isEmpty';
import { TaskCreationPosition } from '../types';
import {
  extractAssignedToInformation,
  generateTask,
  getCreatedByFromProfile,
} from '../../NotebookViewController/utils';
import {
  NOTEBOOK_EVENTS,
  NOTEBOOK_TASK_EVENTS,
} from '../../../../Utils/analytics/constants';
import {
  trackNotebookTaskActionEvent,
  trackNotebookTaskShowEvent,
} from '../../../../Utils/analytics/notebook';
import {
  LayoutStoreState,
  SelectedMember,
} from '../../../../interfaces/Layout';
import useLayoutStore from '../../../../stores/layoutStore';
import { useHistory, useLocation } from 'react-router-dom';
import {
  BoardSectionDropdownOperations,
  BoardTaskDropdownOperations,
  completedSectionFilterOptions,
  sectionDropdownItems,
  taskDropdownItems,
} from './data';
import { DefinedNotebookSections } from '../../../../constants/notebook';
import { NotebookNewSectionCreationData } from '../../NotebookBoardController/useNotebookBoardController';
import { trackNotebookActionEvent } from '../../../../Utils/analytics/notebook';
import { useInviteUserPostQuery } from '../../../../queries/Admin/invites';
import {
  GET_FLOW_BLOCK_PARTICIPANTS,
  GET_MEMBERS,
} from '../../../../constants/endpoints';
import {
  dismissAllToasts,
  showErrorMessage,
  showSuccessMessage,
} from '../../../../Utils/toast';
import { getAssigneesList, getUpdatedNotebookTask } from '../../utils';

const notebookGroupSelector = (store: NotebookStore) => ({
  uploadBoardViewSectionalData: store.uploadBoardViewSectionalData,
  createTaskForBoardView: store.createTaskForBoardView,
  updateTask: store.updateTask,
  editTaskContent: store.editTaskContent,
  archiveTask: store.archiveTask,
  currentView: store.currentView,
  createBoardViewSection: store.createBoardViewSection,
  editBoardViewSectionTitle: store.editBoardViewSectionTitle,
  erroredTaskIDs: store.erroredTaskIDs,
  completedSectionId: store.completedSectionId,
  updateStoreForUnreadTasks: store.updateStoreForUnreadTasks,
});

const layoutStoreSelector = (store: LayoutStoreState) => ({
  setRightAsideOpenToTrue: store.setRightAsideOpenToTrue,
  isRightAsideOpen: store.isRightAsideOpen,
  setTaskIdForNotebook: store.setTaskIdForNotebook,
  removeSelectedMember: store.removeSelectedMember,
  setSelectedMember: store.setSelectedMember,
});

const getTasksAndCountFromQueryData = (
  { pages }: InfiniteData<GetNotebookSectionResponse>,
  timezone: string,
): [NotebookTask[], number] => [
  pages
    .reduce<TaskFromAPI[]>((arr, page) => [...arr, ...page?.data], [])
    .map(
      (taskFromAPI) =>
        convertAPITaskToTaskAndCategory(taskFromAPI, timezone)[0],
    ),
  pages[pages.length - 1]?.total,
];

const useNotebookBoardTasksController = (
  sectionId: string,
  toggleModal?: (
    toggle: boolean,
    sectionId: string | null,
    shouldMakeAPICall?: boolean,
  ) => void,
  setNewSectionCreationData?: (data: NotebookNewSectionCreationData) => void,
  markTasksAsRead?: (taskIds: string[]) => void,
) => {
  const { pathname } = useLocation();
  // Store handlers
  const {
    uploadBoardViewSectionalData,
    updateTask,
    editTaskContent,
    createTaskForBoardView,
    archiveTask,
    createBoardViewSection,
    editBoardViewSectionTitle,
    erroredTaskIDs,
    completedSectionId,
    updateStoreForUnreadTasks,
  } = useNotebookStore(notebookGroupSelector, shallow);

  // Retrieves the sectional data through section ID from the store data object
  const section = useNotebookStore(
    useCallback((store) => store.boardSectionData[sectionId], [sectionId]),
  );

  const { name, count, isCompleted } = section;

  // This is an optimization to prevent re-renders in a section which remains unchanged
  const notes = useNotebookStore(
    useCallback((store) => store.boardNotes[sectionId], [sectionId]),
  );

  const [taskIdFocused, setTaskIdOfFocusedTask] = useState<string>('');
  const [searchValueForAssignees, setSearchValueForAssignees] =
    useState<string>('');

  const [isCreateTaskMode, setCreateTaskMode] = useState(false);
  const [taskCreationPosition, setTaskCreationPosition] = useState<
    TaskCreationPosition | undefined
  >(undefined);

  const [isInEditMode, setEditMode] = useState(false);
  const [sectionTitle, setSectionTitle] = useState(name);
  const [currentCompletedFilter, setCurrentCompletedFilter] = useState<
    string | undefined
  >(undefined);

  const type = useMemo(() => {
    switch (sectionId) {
      case DefinedNotebookSections.Completed:
        return TaskCategories.COMPLETED;
      default:
        return TaskCategories.UNSCHEDULED;
    }
  }, [sectionId]);

  const notesLength = useNotebookStore(
    useCallback(
      (store) => store.boardNotes[sectionId]?.tasks?.length || 0,
      [sectionId],
    ),
  );

  // Querying for assignee list
  const {
    data: assigneesListData,
    isLoading: isAssigneeListLoading,
    hasNextPage: hasMoreAssigneesToFetch,
    isFetchingNextPage: isFetchingMoreAssignees,
    fetchNextPage: fetchMoreAssignees,
  } = useGetNotebookTaskAssigneeList(
    taskIdFocused,
    !isEmpty(taskIdFocused),
    searchValueForAssignees,
  );

  const {
    data: notebookBoardSectionalData,
    isLoading: isTasksLoading,
    isError,
    hasNextPage: hasMoreTasksToFetch,
    isFetchingNextPage: isFetchingMoreTasks,
    fetchNextPage: fetchMoreTasks,
  } = useGetNotebookBoardViewTasksQuery(
    sectionId,
    '',
    count > 0,
    isCompleted ? currentCompletedFilter : undefined,
  );

  const transactionIds = useNotebookStore(
    useCallback(
      (store) =>
        store.transactions.transactionQueue?.map((item) => item?.taskId),
      [],
    ),
  );

  const { data: profileInfo } = useProfileInfoFetchQuery();
  const currentUserTimezone = useMemo(
    () => profileInfo?.member.timeZone || '',
    [profileInfo?.member.timeZone],
  );

  const currentUserId = useMemo(
    () => profileInfo?.member.memberId,
    [profileInfo?.member.memberId],
  );

  const handleSectionMutationSuccess = (sectionID: string) => {
    editBoardViewSectionTitle({
      sectionId: sectionID,
      sectionTitle,
    });
  };

  const { mutate: sectionsMutate, isLoading: isSectionBeingEdited } =
    usePostNotebookSectionsMutation(handleSectionMutationSuccess);

  const dropdownOptions: DropdownOptions[] = useMemo(
    () => [
      {
        id: 'notebook-board-task-options',
        items: taskDropdownItems.filter((item: DropdownItemOption) => {
          return item.id === BoardTaskDropdownOperations.COMPLETE
            ? sectionId !== completedSectionId
            : true;
        }),
      },
    ],
    [sectionId, completedSectionId],
  );

  const sectionDropdownOptions: DropdownOptions[] = useMemo(
    () => [
      {
        id: 'notebook-board-section-options',
        items: sectionDropdownItems?.filter((item) => {
          if (item?.id === BoardSectionDropdownOperations.DELETE_SECTION)
            return sectionId !== completedSectionId;

          return true;
        }),
      },
    ],
    [completedSectionId, sectionId],
  );

  const completedSectionOptions: DropdownOptions[] = useMemo(
    () => [
      {
        id: 'completed-section-options',
        items: completedSectionFilterOptions?.map((i) => {
          if (i?.id === currentCompletedFilter) {
            return {
              ...i,
              isSelected: true,
            };
          }
          return {
            ...i,
            isSelected: false,
          };
        }),
      },
    ],
    [currentCompletedFilter],
  );

  const { push } = useHistory();

  const {
    setRightAsideOpenToTrue,
    isRightAsideOpen,
    setTaskIdForNotebook,
    removeSelectedMember,
    setSelectedMember,
  } = useLayoutStore(layoutStoreSelector, shallow);

  const handleTaskCreationButtonClick = (position: TaskCreationPosition) => {
    setCreateTaskMode((prevState) => !prevState);
    setTaskCreationPosition(position);
  };

  const handleTaskCreation = useCallback(
    (task: NotebookTask) => {
      if (!isNotebookEditorEmpty(task.note)) {
        const assignedTo: Assignee = extractAssignedToInformation(profileInfo);
        const createdBy = getCreatedByFromProfile(profileInfo);
        const taskAnalyticsProps = getNotebookTaskAnalyticsProps(
          task,
          task.type,
          task,
          task.type,
          NotebookViews.Board,
        );
        trackNotebookTaskActionEvent({
          action: NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_CREATE,
          ...taskAnalyticsProps,
        });
        createTaskForBoardView(
          {
            ...task,
            assignedTo,
            createdBy,
            createdAt: new Date().toISOString(),
            type: TaskCategories.UNSCHEDULED,
            dueDate: undefined,
            section: sectionId,
          },
          sectionId,
          taskCreationPosition || TaskCreationPosition.top,
        );
      }
    },
    [createTaskForBoardView, profileInfo, sectionId, taskCreationPosition],
  );

  const handleTaskItemCreation = (content: string) => {
    if (content?.trim()?.length > 0) {
      handleTaskCreation({
        ...generateTask(type, [], currentUserTimezone),
        note: convertStringToTokenizedObject(content),
      });
    }
  };

  const handleTaskCreationTextBlur = (content: string) => {
    handleTaskItemCreation(content);
    setCreateTaskMode(false);
    setTaskCreationPosition(undefined);
  };

  const scrollAddNewTaskInputIntoView = () => {
    const element = document.getElementsByClassName(`section-${sectionId}`);
    if (element.length) {
      setTimeout(() => {
        element[0].scrollTop = element[0].scrollHeight;
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTaskCreationEnterClick = (content: string) => {
    handleTaskItemCreation(content);
    if (taskCreationPosition === 'bottom') {
      scrollAddNewTaskInputIntoView();
    }
    if (!content) {
      setCreateTaskMode(false);
      setTaskCreationPosition(undefined);
    }
  };

  useEffect(() => {
    if (!isTasksLoading && !isError && notebookBoardSectionalData) {
      const data =
        notebookBoardSectionalData as unknown as InfiniteData<GetNotebookSectionResponse>;
      const [tasksArr, totalTasksCount] = getTasksAndCountFromQueryData(
        data,
        currentUserTimezone,
      );
      uploadBoardViewSectionalData(section, tasksArr, totalTasksCount);
    }
  }, [
    section,
    notebookBoardSectionalData,
    isTasksLoading,
    isError,
    currentUserTimezone,
    uploadBoardViewSectionalData,
  ]);

  const handleAssignmentDropdown = (taskId: string, isDelete?: boolean) => {
    if (isDelete) {
      setTaskIdOfFocusedTask('');
    } else {
      setTaskIdOfFocusedTask(taskId);
    }
  };

  const assigneesList = useMemo(() => {
    return getAssigneesList({
      taskIdFocused,
      searchValueForAssignees,
      assigneesListData,
    });
  }, [assigneesListData, searchValueForAssignees, taskIdFocused]);

  const onTaskDataChange = useCallback(
    (data: NotebookAnalyticsParameters | NotebookTaskAnalyticsParameters) => {
      switch (data.action) {
        case NOTEBOOK_EVENTS.NOTEBOOK_TASK_CREATION:
        case NOTEBOOK_EVENTS.NOTEBOOK_SECTION_EXPANDED:
        case NOTEBOOK_EVENTS.NOTEBOOK_SECTION_COLLAPSED:
          {
            const { action } = data as NotebookAnalyticsParameters;
            const notebookAnalyticsProps = getNotebookAnalyticsProps(
              section as unknown as string,
            );
            trackNotebookActionEvent({
              action,
              ...notebookAnalyticsProps,
            });
          }
          break;

        case NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_SHOW:
          {
            const { originalTask, originalCategory } =
              data as NotebookTaskAnalyticsParameters;
            const taskAnalyticsProps = getNotebookTaskAnalyticsProps(
              undefined,
              undefined,
              originalTask,
              originalCategory,
              NotebookViews.Board,
              undefined,
              section.name,
            );
            trackNotebookTaskShowEvent({
              ...taskAnalyticsProps,
              show: NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_SHOW,
            });
          }
          break;

        case NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_DUE_DATE_UPDATE:
          {
            const {
              action,
              originalTask,
              originalCategory,
              updatedTask,
              updatedCategory,
            } = data as NotebookTaskAnalyticsParameters;
            const taskAnalyticsProps = getNotebookTaskAnalyticsProps(
              originalTask,
              originalCategory,
              updatedTask,
              updatedCategory,
              NotebookViews.Board,
              undefined,
              section.name,
            );
            trackNotebookTaskActionEvent({
              action,
              ...taskAnalyticsProps,
            });
          }
          break;

        default:
          return;
      }
    },
    [section],
  );

  const getNotebookBoardTaskAnalyticsAction = useCallback(
    (data: { task: NotebookTask; operation?: NotebookOperations }) => {
      const { task, operation } = data;
      if (operation === NotebookOperations.ASSIGN) {
        return NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_ASSIGNMENT_UPDATE;
      }
      if (operation === NotebookOperations.UPDATE) {
        return NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_SECTION_UPDATED;
      }
      if (task?.type === TaskCategories.ARCHIVED) {
        return NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_ARCHIVE;
      }
      return NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_STATUS_UPDATE;
    },
    [],
  );

  const onUpdateTask = useCallback(
    (
      updatedTask: NotebookTask,
      previousCategory: TaskCategories,
      operation?: NotebookOperations,
    ) => {
      if (profileInfo) {
        const { memberId } = profileInfo.member;
        updateTask(
          updatedTask,
          previousCategory,
          memberId,
          operation === NotebookOperations.ASSIGN,
        );
        const taskAnalyticsProps = getNotebookTaskAnalyticsProps(
          updatedTask,
          previousCategory,
          updatedTask,
          updatedTask.type,
          NotebookViews.Board,
        );
        trackNotebookTaskActionEvent({
          action: getNotebookBoardTaskAnalyticsAction({
            task: updatedTask,
            operation,
          }),
          ...taskAnalyticsProps,
        });
      }
    },
    [getNotebookBoardTaskAnalyticsAction, profileInfo, updateTask],
  );

  const handleTaskUpdate = useCallback(
    (
      updatedTask: NotebookTask,
      previousCategory: TaskCategories,
      updatedCategory: TaskCategories | MiscTaskCategories,
      callback?: (toggle: boolean) => void,
      isAssignment = false,
    ) => {
      onUpdateTask(
        {
          ...updatedTask,
          type: updatedCategory as TaskCategories,
        },
        previousCategory,
        isAssignment ? NotebookOperations.ASSIGN : NotebookOperations.UPDATE,
      );
    },
    [onUpdateTask],
  );

  const { mutate: uploadInviteUserAction } = useInviteUserPostQuery();
  const queryClient = useQueryClient();

  const handleAssigneeChange = useCallback(
    (task: NotebookTask, oldAssignee: Assignee, newAssignee: Assignee) => {
      if (newAssignee?.emailToInvite) {
        const inviteUsers = [{ email: newAssignee.emailToInvite }];
        const inviteUsersPayload = {
          data: inviteUsers,
        };
        uploadInviteUserAction(inviteUsersPayload, {
          onSuccess: (inviteUsersData) => {
            queryClient.invalidateQueries(GET_MEMBERS);
            queryClient.invalidateQueries(GET_FLOW_BLOCK_PARTICIPANTS);
            if (inviteUsersData.data.addedMembers.length) {
              const updatedTask = getUpdatedNotebookTask(inviteUsersData, task);
              handleTaskUpdate(
                updatedTask,
                updatedTask?.type,
                updatedTask?.type,
                () => {},
                true,
              );
              const taskAnalyticsProps = getNotebookTaskAnalyticsProps(
                { ...updatedTask, assignedTo: oldAssignee },
                updatedTask?.type,
                { ...updatedTask, assignedTo: newAssignee },
                updatedTask?.type,
                NotebookViews.Board,
              );
              trackNotebookTaskActionEvent({
                action: NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_ASSIGNMENT_UPDATE,
                ...taskAnalyticsProps,
              });
              dismissAllToasts();
              showSuccessMessage(
                `You successfully invited ${inviteUsersData.data.addedMembers[0].profile.firstName} to the platform!`,
              );
            }
            if (inviteUsersData.data.failedMembers.length) {
              showErrorMessage('Failed to send invite. Please try again');
            }
          },
        });
      } else {
        handleTaskUpdate(task, task?.type, task?.type, () => {}, true);
        const taskAnalyticsProps = getNotebookTaskAnalyticsProps(
          { ...task, assignedTo: oldAssignee },
          task?.type,
          { ...task, assignedTo: newAssignee },
          task?.type,
          NotebookViews.Board,
        );
        trackNotebookTaskActionEvent({
          action: NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_ASSIGNMENT_UPDATE,
          ...taskAnalyticsProps,
        });
      }
    },
    [handleTaskUpdate, queryClient, uploadInviteUserAction],
  );

  const onTaskClick = useCallback(
    (taskId: string) => {
      if (taskId) {
        removeSelectedMember();
        setTaskIdForNotebook(taskId);
        if (!isRightAsideOpen) {
          setRightAsideOpenToTrue();
        }
        push(`${pathname}?taskId=${taskId}`);
      }
    },
    [
      removeSelectedMember,
      setTaskIdForNotebook,
      isRightAsideOpen,
      push,
      pathname,
      setRightAsideOpenToTrue,
    ],
  );

  const handleSectionTitleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSectionTitle(event?.target?.value);
  };

  const editSectionTitle = useCallback(() => {
    if (!isEmpty(sectionTitle) && sectionTitle !== name) {
      trackNotebookActionEvent({
        action: NOTEBOOK_EVENTS.NOTEBOOK_BOARD_SECTION_RENAMED,
        view: NotebookViews.Board,
        sectionName: sectionTitle,
        lastSectionName: name,
      });
      sectionsMutate({
        payload: {
          title: sectionTitle,
          sectionId,
        },
      });
    } else {
      setSectionTitle(name);
    }
  }, [name, sectionId, sectionTitle, sectionsMutate]);

  const handleCompletedFilterSelect = (value: string | number) => {
    if (currentCompletedFilter !== `${value}`) {
      setCurrentCompletedFilter(`${value}`);
      trackNotebookActionEvent({
        action: NOTEBOOK_EVENTS.NOTEBOOK_BOARD_SECTION_COMPLETION_FILTER,
        view: NotebookViews.Board,
        completedFilter: `${value}`,
      });
    } else {
      setCurrentCompletedFilter(undefined);
    }
  };

  const handleCreatorLinkClick = useCallback(
    (memberData: SelectedMember) => {
      setSelectedMember(memberData);
      if (!isRightAsideOpen) {
        setRightAsideOpenToTrue();
      }
    },
    [isRightAsideOpen, setRightAsideOpenToTrue, setSelectedMember],
  );

  const handleSectionMenuItemClick = useCallback(
    (value: string | number) => {
      const operation = value as BoardSectionDropdownOperations;
      switch (operation) {
        case BoardSectionDropdownOperations.RENAME_SECTION: {
          setEditMode(true);
          break;
        }

        case BoardSectionDropdownOperations.ADD_SECTION_TO_LEFT: {
          if (setNewSectionCreationData) {
            setNewSectionCreationData({
              currentSectionId: sectionId,
              position: 'left',
            });
          }
          break;
        }

        case BoardSectionDropdownOperations.ADD_SECTION_TO_RIGHT: {
          if (setNewSectionCreationData) {
            setNewSectionCreationData({
              currentSectionId: sectionId,
              position: 'right',
            });
          }
          break;
        }

        case BoardSectionDropdownOperations.DELETE_SECTION:
          if (toggleModal)
            toggleModal(
              count > 0 || notesLength > 0,
              sectionId,
              count < 1 && notesLength < 1,
            );
          break;

        default:
          break;
      }
    },
    [toggleModal, setNewSectionCreationData, count, notesLength, sectionId],
  );

  const handleSectionTitleFocus = useCallback(() => {
    editSectionTitle();
    setEditMode(false);
  }, [editSectionTitle]);

  const updateReadStatusOfTask = (
    taskId: string,
    noteId: string,
    taskCategory: TaskCategories,
  ) => {
    updateStoreForUnreadTasks(taskId, taskCategory);
    if (markTasksAsRead) markTasksAsRead([noteId]);
  };

  return {
    notes: notes || [],
    section,
    hasMoreAssigneesToFetch: hasMoreAssigneesToFetch || false,
    isFetchingMoreAssignees: isAssigneeListLoading || isFetchingMoreAssignees,
    searchValue: searchValueForAssignees,
    handleSearchOnAssigneeList: setSearchValueForAssignees,
    handleAssigneeChange,
    setTaskIdOfFocusedTask: handleAssignmentDropdown,
    fetchMoreAssignees,
    isAssigneeListLoading,
    assigneesList,
    editTaskContent,
    updateTask: handleTaskUpdate,
    handleTaskCreationButtonClick,
    handleTaskCreationTextBlur,
    handleTaskCreationEnterClick,
    isCreateTaskMode,
    taskCreationPosition,
    currentUserId,
    currentUserTimezone,
    hasMoreTasksToFetch,
    isFetchingMoreTasks,
    fetchMoreTasks,
    isTasksLoading,
    onTaskClick,
    dropdownOptions,
    archiveTask,
    createOrEditBoardViewSection: createBoardViewSection,
    sectionDropdownOptions,
    editSectionTitle,
    isInEditMode,
    sectionTitle,
    setEditMode,
    handleSectionTitleChange,
    isSectionBeingEdited,
    handleSectionMenuItemClick,
    completedSectionOptions,
    handleCompletedFilterSelect,
    notesLength,
    transactionIds,
    erroredTaskIDs,
    handleSectionTitleFocus,
    handleCreatorLinkClick,
    updateReadStatusOfTask,
    onTaskDataChange,
  };
};

export default useNotebookBoardTasksController;
